/**
 * Persistence for an in-progress observation.
 *
 * The observation flow spans five screens. Without this, a refresh — or an
 * accidental back-navigation — loses every note the observer has taken, and
 * the later screens then fail because there is nothing to work from.
 *
 * sessionStorage, not localStorage: an observation belongs to the tab it was
 * started in, and should not outlive the browser session.
 */

export const STORAGE_KEY = 'elo_obs_state';

/** Only the state worth restoring. Timer handles are not serialisable. */
export const PERSISTED_KEYS = [
  'obs', 'teacher', 'notes', 'seconds',
  'finalBest', 'finalBetter',
  'statements', 'closeOutcome',
  'nextStep', 'conclusions',
];

function store() {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;   // Private browsing / storage disabled.
  }
}

/** Read the saved state, or null if there is nothing usable. */
export function loadObsState() {
  const s = store();
  if (!s) return null;
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // An observation without an id cannot be resumed — the later screens all
    // key off it, so treat it as nothing rather than half-restoring.
    if (!parsed || typeof parsed !== 'object' || !parsed.obs?.id) return null;
    return parsed;
  } catch {
    return null;   // Corrupt payload — start clean rather than crash on boot.
  }
}

/** Write the state, keeping only the keys worth restoring. */
export function saveObsState(state) {
  const s = store();
  if (!s) return;
  try {
    if (!state?.obs?.id) { s.removeItem(STORAGE_KEY); return; }
    const slice = {};
    for (const key of PERSISTED_KEYS) {
      if (state[key] !== undefined) slice[key] = state[key];
    }
    s.setItem(STORAGE_KEY, JSON.stringify(slice));
  } catch {
    // Quota or serialisation failure — losing persistence is survivable,
    // crashing the observation is not.
  }
}

export function clearObsState() {
  const s = store();
  if (!s) return;
  try { s.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

/** Server evidence note → the shape the observation screens use. */
export function noteFromApi(n) {
  return {
    id: n.id,
    who: n.student_group,
    studentId: n.student_identifier || '',
    idType: n.student_identifier_type || 'name',
    exceptions: n.group_exceptions || '',
    side: n.learning_effective ? 'pos' : 'neg',
    evidence: n.evidence_text,
    impact: n.impact_text,
    bucket: n.bucket,
    timestamp: n.timestamp_seconds ?? 0,
  };
}
